import { StopOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Modal, Select, Skeleton, Tabs, Typography } from 'antd';
import { useGetMyArtifactSharesQuery, useManageMyArtifactShareMutation } from 'api/ArtifactsApi';
import { useGetAllContactsQuery } from 'api/ContactsApi';
import { ArtifactShare } from 'components/Artifacts/types';
import { useEffect, useMemo, useState } from 'react';

const { Paragraph, Text, Title } = Typography;
const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

type ArtifactShareModalProps = {
  open: boolean;
  collectionCount: number;
  onClose: () => void;
};

const upsertShare = (shares: ArtifactShare[], nextShare: ArtifactShare | null) => {
  if (!nextShare) {
    return shares;
  }

  const existingIndex = shares.findIndex((share) => share.id === nextShare.id);

  if (existingIndex < 0) {
    return [nextShare, ...shares];
  }

  return shares.map((share) => (share.id === nextShare.id ? nextShare : share));
};

const removeShare = (shares: ArtifactShare[], shareId: string) => shares.filter((share) => share.id !== shareId);

export function ArtifactShareModal({ open, collectionCount, onClose }: ArtifactShareModalProps) {
  const { data, isLoading, isFetching, isError, refetch } = useGetMyArtifactSharesQuery(undefined, {
    skip: !open,
    refetchOnFocus: true,
    refetchOnReconnect: true
  });
  const { data: contactsData, isLoading: isLoadingContacts } = useGetAllContactsQuery({}, { skip: !open });
  const [manageShare, { isLoading: isMutating }] = useManageMyArtifactShareMutation();
  const [currentShares, setCurrentShares] = useState<ArtifactShare[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>();
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [showRecipientNameError, setShowRecipientNameError] = useState(false);
  const [showRecipientEmailError, setShowRecipientEmailError] = useState(false);
  const [createFeedback, setCreateFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [activeFeedback, setActiveFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [activeTabKey, setActiveTabKey] = useState('new-share');

  useEffect(() => {
    if (!open) {
      setCreateFeedback(null);
      setActiveFeedback(null);
      return;
    }

    setCurrentShares(data || []);
    setCreateFeedback(null);
    setActiveFeedback(null);
  }, [data, open]);

  const isBusy = isMutating || isFetching;
  const helperText = useMemo(() => {
    if (!collectionCount) {
      return 'You can still share now. Recipients will see a safe empty state until stories are added.';
    }

    return `Each share sends access to your full artifact collection. Right now it includes ${collectionCount} artifact${collectionCount === 1 ? '' : 's'}.`;
  }, [collectionCount]);
  const contactOptions = useMemo(
    () =>
      (contactsData?.list || []).map((contact) => ({
        value: contact.id,
        label: contact.name,
        contact
      })),
    [contactsData]
  );
  const activeShares = useMemo(() => currentShares.filter((share) => share.status === 'ACTIVE' && !share.revokedAt), [currentShares]);

  const runShareAction = async (
    action: 'REVOKE' | 'RESEND',
    successMessage: string,
    payload: { id: string; expiresAt?: string | null }
  ) => {
    try {
      setActiveFeedback(null);
      const nextShare = await manageShare({
        action,
        shareId: payload.id,
        expiresAt: payload?.expiresAt
      }).unwrap();

      setCurrentShares((previousShares) => {
        if (action === 'REVOKE') {
          if (!nextShare || nextShare.status === 'REVOKED' || nextShare.revokedAt) {
            return removeShare(previousShares, payload.id);
          }
        }

        return upsertShare(previousShares, nextShare);
      });
      setActiveFeedback({ type: 'success', message: successMessage });
      void refetch();
    } catch (error: any) {
      setActiveFeedback({ type: 'error', message: error?.data?.message || 'We could not update this share right now. Please try again.' });
    }
  };

  const handleContactChange = (contactId?: string) => {
    setSelectedContactId(contactId);

    const selectedContact = contactOptions.find((contact) => contact.value === contactId)?.contact;
    if (!selectedContact) {
      return;
    }

    setRecipientName(selectedContact.name || '');
    setRecipientEmail(selectedContact.email || '');
    setShowRecipientNameError(false);
    setShowRecipientEmailError(false);
  };

  const handleCreateShare = async () => {
    const trimmedRecipientName = recipientName.trim();
    const trimmedRecipientEmail = recipientEmail.trim();
    const hasRecipientNameError = !trimmedRecipientName;
    const hasRecipientEmailError = !isValidEmail(trimmedRecipientEmail);

    setShowRecipientNameError(hasRecipientNameError);
    setShowRecipientEmailError(hasRecipientEmailError);
    setCreateFeedback(null);

    if (hasRecipientNameError || hasRecipientEmailError) {
      return;
    }

    try {
      const nextShare = await manageShare({
        action: 'CREATE',
        recipientName: trimmedRecipientName,
        recipientEmail: trimmedRecipientEmail
      }).unwrap();

      setCurrentShares((previousShares) => upsertShare(previousShares, nextShare));
      setSelectedContactId(undefined);
      setRecipientName('');
      setRecipientEmail('');
      setActiveTabKey('active-shares');
      setActiveFeedback({ type: 'success', message: 'Share created.' });
      void refetch();
    } catch (error: any) {
      setCreateFeedback({ type: 'error', message: error?.data?.message || 'We could not create this share right now. Please try again.' });
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} centered width={640} title={null} destroyOnHidden className="artifact-share-modal">
      <div className="artifact-share-modal__body">
        <Title level={3} style={{ marginBottom: 8 }}>
          Share your artifact collection
        </Title>
        <Paragraph className="artifact-share-modal__subheader">Send secure access to the full collection without making anyone sign in.</Paragraph>
        <Paragraph>{helperText}</Paragraph>

        <Tabs
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          className="artifact-share-modal__tabs"
          items={[
            {
              key: 'new-share',
              label: 'New share',
              children: (
                <div className="artifact-share-modal__form">
                  <label className="artifact-share-modal__field-label" htmlFor="artifact-share-contact">
                    Contact
                  </label>
                  <Select
                    id="artifact-share-contact"
                    allowClear
                    showSearch
                    placeholder={isLoadingContacts ? 'Loading contacts...' : 'Choose a saved contact'}
                    options={contactOptions}
                    value={selectedContactId}
                    onChange={(value) => handleContactChange(value || undefined)}
                    disabled={isBusy || isLoadingContacts || !contactOptions.length}
                    optionFilterProp="label"
                  />

                  <label className="artifact-share-modal__field-label" htmlFor="artifact-share-recipient-name">
                    Name
                  </label>
                  <Input
                    id="artifact-share-recipient-name"
                    placeholder="Enter recipient name"
                    value={recipientName}
                    onChange={(event) => {
                      setRecipientName(event.target.value);
                      if (showRecipientNameError) {
                        setShowRecipientNameError(false);
                      }
                    }}
                    status={showRecipientNameError ? 'error' : undefined}
                    disabled={isBusy}
                  />
                  {showRecipientNameError && <p className="artifact-share-modal__field-error">Name is required.</p>}

                  <label className="artifact-share-modal__field-label" htmlFor="artifact-share-recipient-email">
                    Email
                  </label>
                  <Input
                    id="artifact-share-recipient-email"
                    type="email"
                    placeholder="Enter recipient email"
                    value={recipientEmail}
                    onChange={(event) => {
                      setRecipientEmail(event.target.value);
                      if (showRecipientEmailError) {
                        setShowRecipientEmailError(false);
                      }
                    }}
                    status={showRecipientEmailError ? 'error' : undefined}
                    disabled={isBusy}
                  />
                  {showRecipientEmailError && <p className="artifact-share-modal__field-error">Enter a valid email address.</p>}

                  {createFeedback && <Alert type={createFeedback.type} showIcon message={createFeedback.message} />}

                  <div className="artifact-share-modal__actions">
                    <Button onClick={onClose} disabled={isBusy}>
                      Cancel
                    </Button>
                    <Button type="primary" onClick={() => void handleCreateShare()} loading={isBusy} disabled={isBusy}>
                      Share now
                    </Button>
                  </div>
                </div>
              )
            },
            {
              key: 'active-shares',
              label: 'Active shares',
              children: (
                <div className="artifact-share-active-listing">
                  <Text strong>Active shared links</Text>

                  {activeFeedback && <Alert type={activeFeedback.type} showIcon message={activeFeedback.message} />}

                  {isLoading && <Skeleton active paragraph={{ rows: 4 }} />}

                  {!isLoading && isError && (
                    <Alert
                      type="error"
                      showIcon
                      message="We couldn’t load your active shares."
                      action={
                        <Button size="small" onClick={() => refetch()}>
                          Refresh
                        </Button>
                      }
                    />
                  )}

                  {!isLoading && !isError && !activeShares.length && (
                    <p className="artifact-share-active-listing__empty">No active shares yet.</p>
                  )}

                  {!isLoading && !isError && activeShares.length > 0 && (
                    <div className="artifact-share-active-listing__items">
                      {activeShares.map((share) => {
                        return (
                          <div className="artifact-share-active-listing__item" key={share.id}>
                            <div className="artifact-share-active-listing__copy">
                              <p className="artifact-share-active-listing__name">{share.recipientName || 'Recipient'}</p>
                              <p className="artifact-share-active-listing__email">{share.recipientEmail || 'No email provided'}</p>
                            </div>

                            <div className="artifact-share-active-listing__actions">
                              <Button
                                size="small"
                                onClick={() => void runShareAction('RESEND', 'Share resent.', { id: share.id })}
                                loading={isBusy}
                                disabled={isBusy}
                              >
                                Resend
                              </Button>
                              <Button
                                size="small"
                                danger
                                icon={<StopOutlined />}
                                onClick={() => void runShareAction('REVOKE', 'Share revoked.', { id: share.id })}
                                loading={isBusy}
                              >
                                Delete share
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="artifact-share-modal__actions">
                    <Button onClick={onClose} disabled={isBusy}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )
            }
          ]}
        />
      </div>
    </Modal>
  );
}

export default ArtifactShareModal;